<p-toast></p-toast>

<app-page-shell>

    <app-page-hero
        eyebrow="CRM Contracts"
        [title]="createEditContract.contractID ? 'Edit Commercial Agreement' : 'Create Commercial Agreement'"
        [subtitle]="createEditContract.contractID ? 'Update seller and buyer party ownership without changing lifecycle behavior.' : 'Create the agreement shell before adding revisions, files, and rate cards.'">
        <div actions>
            <p-button label="Clear" icon="fa-solid fa-xmark" severity="secondary" [outlined]="true" (onClick)="clearContractForm()"></p-button>
            <p-button [label]="createEditContract.contractID ? 'Save Changes' : 'Create Contract'"
                [icon]="createEditContract.contractID ? 'fa-solid fa-floppy-disk' : 'fa-solid fa-plus'"
                [loading]="contractCreationInProgress" [disabled]="isCreateDisabled()" (onClick)="createUpdateContract()"></p-button>
        </div>
    </app-page-hero>

    <form [formGroup]="createEditContractForm" novalidate autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="off">
        <div class="p-fluid flex flex-column gap-3">
            <app-page-form-section
                icon="fa-solid fa-file-contract"
                title="Contract Header"
                subtitle="Confirm generated contract identity and the current revision-derived status.">
                <div class="formgrid grid">
                    <div class="field col-12 md:col-6">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <input id="contractNumber" type="text" pInputText readonly class="p-disabled"
                                    placeholder="Auto-generated on save" formControlName="contractNumberInput" />
                                <label for="contractNumber">Contract Number</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-6">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-circle-info"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <input id="contractStatus" type="text" pInputText readonly class="p-disabled"
                                    [value]="getCurrentStatus()" />
                                <label for="contractStatus">Current Status</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                </div>
            </app-page-form-section>

            <app-page-form-section
                icon="fa-solid fa-flag-checkered"
                title="Seller (First Party)"
                subtitle="Select whether the seller is your company or a CRM supplier, then confirm the representative.">
                <div class="formgrid grid">
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-shuffle"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select id="firstPartyMode" [options]="firstPartyModeOptions" optionLabel="label" optionValue="value"
                                    [(ngModel)]="firstPartyMode" [ngModelOptions]="{ standalone: true }"
                                    (onChange)="onFirstPartyModeChanged($event.value)" styleClass="w-full">
                                </p-select>
                                <label for="firstPartyMode">Seller Source</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-4">
                        <ng-container *ngIf="firstPartyMode === 'SELF'; else sellerCrmSelect">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="fa-solid fa-building"></i></p-inputGroupAddon>
                                <p-iftalabel>
                                    <input id="firstPartySelf" type="text" pInputText readonly class="p-disabled"
                                        [value]="getResolvedCompany('firstParty')?.name || 'My company'" />
                                    <label for="firstPartySelf">Seller Company</label>
                                </p-iftalabel>
                            </p-inputGroup>
                        </ng-container>
                        <ng-template #sellerCrmSelect>
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="fa-solid fa-building"></i></p-inputGroupAddon>
                                <p-iftalabel>
                                    <p-select id="firstPartyCrm" [options]="firstPartyCrmOptions" optionLabel="label" dataKey="key"
                                        [filter]="true" filterBy="label,companyLabel" [showClear]="true"
                                        [(ngModel)]="selectedFirstPartyCrmOption" [ngModelOptions]="{ standalone: true }"
                                        (onChange)="onFirstPartyCrmOptionChanged($event.value)" styleClass="w-full">
                                    </p-select>
                                    <label for="firstPartyCrm">CRM Supplier</label>
                                </p-iftalabel>
                            </p-inputGroup>
                        </ng-template>
                    </div>
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-user-tie"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select id="firstPartyRep" [options]="firstPartyReps" optionLabel="name" dataKey="id"
                                    [filter]="true" filterBy="name" [showClear]="true"
                                    formControlName="firstPartyRepDropdown" styleClass="w-full">
                                </p-select>
                                <label for="firstPartyRep">Seller Representative</label>
                            </p-iftalabel>
                            <p-inputGroupAddon *ngIf="canRegisterPartyRep('firstParty')">
                                <p-button icon="pi pi-plus" severity="secondary" (onClick)="registerPartyRep('firstParty')"></p-button>
                            </p-inputGroupAddon>
                        </p-inputGroup>
                    </div>
                </div>
            </app-page-form-section>

            <app-page-form-section
                icon="fa-solid fa-flag"
                title="Buyer (Second Party)"
                subtitle="Select whether the buyer is your company or a CRM client, then confirm the representative.">
                <div class="formgrid grid">
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-shuffle"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select id="secondPartyMode" [options]="secondPartyModeOptions" optionLabel="label" optionValue="value"
                                    [(ngModel)]="secondPartyMode" [ngModelOptions]="{ standalone: true }"
                                    (onChange)="onSecondPartyModeChanged($event.value)" styleClass="w-full">
                                </p-select>
                                <label for="secondPartyMode">Buyer Source</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-4">
                        <ng-container *ngIf="secondPartyMode === 'SELF'; else buyerCrmSelect">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="fa-solid fa-building"></i></p-inputGroupAddon>
                                <p-iftalabel>
                                    <input id="secondPartySelf" type="text" pInputText readonly class="p-disabled"
                                        [value]="getResolvedCompany('secondParty')?.name || 'My company'" />
                                    <label for="secondPartySelf">Buyer Company</label>
                                </p-iftalabel>
                            </p-inputGroup>
                        </ng-container>
                        <ng-template #buyerCrmSelect>
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="fa-solid fa-building"></i></p-inputGroupAddon>
                                <p-iftalabel>
                                    <p-select id="secondPartyCrm" [options]="secondPartyCrmOptions" optionLabel="label" dataKey="key"
                                        [filter]="true" filterBy="label,companyLabel" [showClear]="true"
                                        [(ngModel)]="selectedSecondPartyCrmOption" [ngModelOptions]="{ standalone: true }"
                                        (onChange)="onSecondPartyCrmOptionChanged($event.value)" styleClass="w-full">
                                    </p-select>
                                    <label for="secondPartyCrm">CRM Client</label>
                                </p-iftalabel>
                            </p-inputGroup>
                        </ng-template>
                    </div>
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-user-tie"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select id="secondPartyRep" [options]="secondPartyReps" optionLabel="name" dataKey="id"
                                    [filter]="true" filterBy="name" [showClear]="true"
                                    formControlName="secondPartyRepDropdown" styleClass="w-full">
                                </p-select>
                                <label for="secondPartyRep">Buyer Representative</label>
                            </p-iftalabel>
                            <p-inputGroupAddon *ngIf="canRegisterPartyRep('secondParty')">
                                <p-button icon="pi pi-plus" severity="secondary" (onClick)="registerPartyRep('secondParty')"></p-button>
                            </p-inputGroupAddon>
                        </p-inputGroup>
                    </div>
                </div>
            </app-page-form-section>

            <app-page-form-section
                icon="fa-solid fa-diagram-project"
                title="Commercial Context"
                subtitle="Read-only CRM lineage used to keep account ownership, documents, reminders, and quotation context visible.">
                <div class="formgrid grid">
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-building-user"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <input id="crmClient" type="text" pInputText readonly class="p-disabled"
                                    [value]="crmContext?.client?.companyName || (createEditContract.crmClientId ? 'CRM Client #' + createEditContract.crmClientId : 'Not linked')" />
                                <label for="crmClient">CRM Client</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-file-invoice-dollar"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <input id="sourceQuotation" type="text" pInputText readonly class="p-disabled"
                                    [value]="createEditContract.sourceQuotationId ? 'Quotation #' + createEditContract.sourceQuotationId : (crmContext?.approvedQuotationAwaitingContractCount || 0) + ' approved awaiting contract'" />
                                <label for="sourceQuotation">Source Quotation</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-user-check"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <input id="contractOwner" type="text" pInputText readonly class="p-disabled"
                                    [value]="createEditContract.kamCustomerId ? 'KAM #' + createEditContract.kamCustomerId : 'Uses existing party ownership'" />
                                <label for="contractOwner">KAM / Owner</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                </div>

                <div *ngIf="isCrmContextLoading" class="text-600 mt-2">
                    <i class="pi pi-spin pi-spinner mr-2"></i>Loading CRM context...
                </div>

                <div *ngIf="crmContext" class="grid mt-2">
                    <div class="col-12 md:col-3">
                        <div class="surface-50 border-1 surface-border border-round p-3 h-full">
                            <div class="font-bold mb-2"><i class="fa-solid fa-folder-open mr-2"></i>Documents</div>
                            <div>{{ (crmContext.documents?.length || 0) + (crmContext.registrationAttachments?.length || 0) }} CRM files</div>
                        </div>
                    </div>
                    <div class="col-12 md:col-3">
                        <div class="surface-50 border-1 surface-border border-round p-3 h-full">
                            <div class="font-bold mb-2"><i class="fa-solid fa-bell mr-2"></i>Reminders</div>
                            <div>{{ crmContext.reminders?.length || 0 }} open or historical tasks</div>
                        </div>
                    </div>
                    <div class="col-12 md:col-3">
                        <div class="surface-50 border-1 surface-border border-round p-3 h-full">
                            <div class="font-bold mb-2"><i class="fa-solid fa-file-signature mr-2"></i>Contracts</div>
                            <div>{{ crmContext.linkedContractCount || 0 }} linked agreements</div>
                        </div>
                    </div>
                    <div class="col-12 md:col-3">
                        <div class="surface-50 border-1 surface-border border-round p-3 h-full">
                            <div class="font-bold mb-2"><i class="fa-solid fa-heart-pulse mr-2"></i>Health</div>
                            <div class="flex flex-wrap gap-2">
                                <p-tag *ngFor="let flag of getHealthFlags()" [value]="flag" severity="info"></p-tag>
                                <span *ngIf="getHealthFlags().length === 0">No flags</span>
                            </div>
                        </div>
                    </div>
                </div>
            </app-page-form-section>
        </div>
    </form>
</app-page-shell>

<app-customer-form [displayModal]="showCustomerForm" [dialogHeader]="customerFormHeader"
    [submitButtonLabel]="customerFormSubmitLabel" [company]="selectedCompany" [customer]="selectedCustomer"
    (modalClosed)="onCustomerFormClosed()">
</app-customer-form>
